/**
 * Gap Scanner — pick a market, then see what is wrong inside it.
 *
 * The page is one drill-down, not a list of every (market, line) finding. A
 * reader works a market at a time, so markets are the top level and everything
 * else lives under the one they open:
 *
 *   1. which categories fall short there, each against the network norm
 *   2. which distributors carry those categories, and which are under-indexing
 *   3. every distributor in the market, gap or no gap
 *   4. the ship-tos behind them
 *
 * **Consolidator ship-tos are not here.** A branch booked to a drop point in
 * another state is excluded from the market rather than flagged inside it —
 * Vancouver and Edmonton are not Los Angeles, and counting them there makes the
 * market look bigger than its restaurants can explain. They are listed at the
 * foot of the page so the exclusion is visible rather than a quiet subtraction.
 *
 * **The benchmark is leave-one-out.** A market is measured against what the same
 * distributors sell of that line across every *other* market, so it is never
 * compared with a number it is itself inside.
 *
 * Nothing is computed here; every figure arrives in the payload.
 */

import { Fragment, useState } from 'react'

import type { CustomReportProps } from '..'
import type { Cell } from '../../lib/types'
import { Card, Empty } from '../shared/Card'
import { count, dataset, type Keyed, text } from '../shared/rows'

import '../report.css'

import { AssortmentChart, type AssortmentRow } from './AssortmentChart'
import { Composition, type CompositionSlice, GapBars, type GapBar } from './GapBars'

const NONE = '—'
const NOT_CARRYING = 'not carrying'
const UNDERWEIGHT = 'underweight'

function num(value: Cell | undefined): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function gal(value: number | null): string {
  return value === null ? NONE : Math.round(value).toLocaleString('en-US')
}

/** Exposure, not revenue: A4 prices risk at a blended rate, never the account's
 *  own price. Labelled as exposure everywhere it appears. */
function money(value: number | null): string {
  return value === null ? NONE : `$${Math.round(value).toLocaleString('en-US')}`
}

function pct(value: number | null): string {
  return value === null ? NONE : `${value.toFixed(1)}%`
}

export function GapScanner({ result }: CustomReportProps) {
  const markets = dataset(result, 'markets')
  const marketDistributors = dataset(result, 'market_distributors')
  const marketShipTos = dataset(result, 'market_ship_tos')
  const gaps = dataset(result, 'gaps')
  const gapDistributors = dataset(result, 'distributors')
  const gapByLine = dataset(result, 'gap_by_line')
  const excluded = dataset(result, 'redistribution')
  const marketLines = dataset(result, 'market_lines')
  const shipToLines = dataset(result, 'ship_to_lines')
  const marketLineDistributors = dataset(result, 'market_line_distributors')
  const asOf = dataset(result, 'as_of')

  const [selected, setSelected] = useState<string | null>(null)
  const [shipTo, setShipTo] = useState<string | null>(null)
  const pick = (market: string) => {
    setSelected((current) => (current === market ? null : market))
  }

  const lineSlices: CompositionSlice[] = gapByLine.map((row) => ({
    bucket: text(row['bucket']) ?? NONE,
    gaps: count(row['gaps']),
    gallons: num(row['gallons_at_risk']),
    sharePct: num(row['share_pct']),
  }))

  const gapsFor = (market: string): GapBar[] =>
    gaps
      .filter((row) => text(row['cbsa_title']) === market)
      .map((row, index) => ({
        key: `${market}-${text(row['product_line']) ?? ''}-${String(index)}`,
        market,
        line: text(row['product_line']) ?? NONE,
        gapType: text(row['gap_type']) === 'market' ? 'market gap' : 'account gap',
        metroPct: num(row['metro_pct']),
        normPct: num(row['channel_norm_pct']),
        pctOfNorm: num(row['pct_of_channel_norm']),
        gallonsAtRisk: num(row['gallons_at_risk']),
        distributors: count(row['distributors_in_metro']),
        notCarrying: count(row['zero_carry_count']),
      }))

  const rowsFor = (rows: Keyed[], market: string): Keyed[] =>
    rows.filter((row) => text(row['cbsa_title']) === market)

  const selectedGaps = selected === null ? [] : gapsFor(selected)
  const selectedGapLines = selected === null ? [] : rowsFor(gapDistributors, selected)
  const selectedDistributors = selected === null ? [] : rowsFor(marketDistributors, selected)
  const selectedShipTos = selected === null ? [] : rowsFor(marketShipTos, selected)
  const selectedLines = selected === null ? [] : rowsFor(marketLines, selected)
  const selectedLineWho = selected === null ? [] : rowsFor(marketLineDistributors, selected)

  // Which ship-to inside the market is open. Derived rather than trusted: a
  // branch selected in a previous market would otherwise stay open and have its
  // mix read against the wrong market.
  const openShipTo =
    shipTo !== null && selectedShipTos.some((row) => text(row['mkt_ship_to']) === shipTo)
      ? shipTo
      : null
  const shipToMix =
    selected === null || openShipTo === null
      ? []
      : rowsFor(shipToLines, selected).filter((row) => text(row['mkt_ship_to']) === openShipTo)

  // The market's own headline row, for the figures above the assortment chart.
  const selectedMarket = markets.find((row) => text(row['cbsa_title']) === selected)

  // A4 prices each gap at a blended rate, so this ranks metros against each
  // other -- it is not this metro's booked revenue.
  const dollarsAtRisk =
    selected === null
      ? 0
      : rowsFor(gaps, selected).reduce((sum, row) => sum + (num(row['dollars_at_risk']) ?? 0), 0)

  // Every category, not only the flagged ones -- a line at zero is the most
  // important row here and a shortfall list cannot contain it.
  const assortment: AssortmentRow[] = selectedLines.map((row) => ({
    line: text(row['mkt_product_line']) ?? '',
    gallons: num(row['gallons_ttm']),
    expected: num(row['expected_gal']),
    vsNorm: num(row['vs_norm_gal']),
    pctOfNorm: num(row['pct_of_norm']),
    distributors: count(row['distributors']),
    hasGap: text(row['has_gap']) === 'yes',
  }))

  return (
    <div className="bespoke-report gs-page">
      <Card
        title="Where the gap is, by category"
        subtitle="Across every market. Open a market below to see which categories fall short there."
      >
        <Composition slices={lineSlices} />
      </Card>

      <Card
        title="Markets"
        subtitle={
          'Street business only — Open FS Operator through Broadline. Chain volume routed ' +
          'through a distributor, Club volume, and branches booked to a consolidator in ' +
          'another state are all excluded. Click a market to open it.'
        }
      >
        {markets.length === 0 ? (
          <Empty label="No street business in scope." />
        ) : (
          <div className="gs-scroll">
            <table className="gs-table">
              <thead>
                <tr>
                  <th scope="col" className="gs-col-market">
                    Market
                  </th>
                  <th scope="col" className="cell-number">
                    Street gal TTM
                  </th>
                  <th scope="col" className="cell-number">
                    Distributors
                  </th>
                  <th scope="col" className="cell-number">
                    Ship-tos
                  </th>
                  <th scope="col" className="cell-number">
                    Lines
                  </th>
                  <th scope="col" className="cell-number">
                    Open gaps
                  </th>
                  <th scope="col" className="cell-number">
                    Gal at risk
                  </th>
                </tr>
              </thead>
              <tbody>
                {markets.map((row) => {
                  const market = text(row['cbsa_title']) ?? ''
                  const isOpen = selected === market
                  const openGaps = count(row['gaps'])
                  return (
                    <tr key={market} className={isOpen ? 'gs-market is-open' : 'gs-market'}>
                      <td className="gs-col-market">
                        <button
                          type="button"
                          className="gs-disclose"
                          aria-expanded={isOpen}
                          onClick={() => {
                            pick(market)
                          }}
                        >
                          <span className="gs-caret" aria-hidden="true">
                            {isOpen ? '▾' : '▸'}
                          </span>
                          {market}
                        </button>
                      </td>
                      <td className="cell-number">{gal(num(row['gallons_ttm']))}</td>
                      <td className="cell-number">{count(row['distributors'])}</td>
                      <td className="cell-number">{count(row['ship_tos'])}</td>
                      <td className="cell-number">{count(row['lines'])}</td>
                      <td className="cell-number">
                        {openGaps > 0 ? <span className="gs-gapcount">{openGaps}</span> : ''}
                      </td>
                      <td className="cell-number">{gal(num(row['gallons_at_risk']))}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected !== null && (
        <Card
          title={selected}
          subtitle={
            'Every category this metro buys and every one the channel sells that it does not, ' +
            'against what the channel rate would predict here. The benchmark excludes this ' +
            'metro, so a market big enough to move the average is never measured against itself.'
          }
          actions={
            <button
              type="button"
              className="gs-clear"
              onClick={() => {
                setSelected(null)
              }}
            >
              Close
            </button>
          }
        >
          {selectedMarket !== undefined && (
            <div className="gs-market-kpis">
              <div>
                <p className="gs-kpi-value">{money(dollarsAtRisk)}</p>
                <p className="gs-kpi-label">exposure in this metro</p>
              </div>
              <div>
                <p className="gs-kpi-value">{count(selectedMarket['gaps'])}</p>
                <p className="gs-kpi-label">line gaps</p>
              </div>
              <div>
                <p className="gs-kpi-value">{gal(num(selectedMarket['gallons_ttm']))}</p>
                <p className="gs-kpi-label">metro gallons, trailing year</p>
              </div>
              <div>
                <p className="gs-kpi-value">{count(selectedMarket['distributors'])}</p>
                <p className="gs-kpi-label">distributors here</p>
              </div>
            </div>
          )}

          <AssortmentChart rows={assortment} />

          {selectedGaps.length > 0 && (
            <>
              <section className="gs-detail-section">
                <h3 className="gs-comp-title">The flagged lines, against the network</h3>
                <GapBars gaps={selectedGaps} onPick={pick} />
              </section>

              <section className="gs-detail-section">
                <h3 className="gs-comp-title">
                  Who carries these categories here, and who is under-indexing
                </h3>
                <table className="gs-table">
                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col">Distributor</th>
                      <th scope="col">Carrying</th>
                      <th scope="col" className="cell-number">
                        Actual
                      </th>
                      <th scope="col" className="cell-number">
                        Expected
                      </th>
                      <th scope="col" className="cell-number">
                        Shortfall
                      </th>
                      <th scope="col" className="cell-number">
                        % of its own norm
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedGapLines.map((row, index) => {
                      const state = text(row['carry_state'])
                      const line = text(row['product_line']) ?? ''
                      const who = text(row['distributor']) ?? ''
                      return (
                        <tr key={`${line}-${who}-${String(index)}`}>
                          <td>{line || NONE}</td>
                          <td>{who || NONE}</td>
                          <td>
                            {state === NOT_CARRYING && (
                              <span className="gs-flag is-none">not carrying</span>
                            )}
                            {state === UNDERWEIGHT && (
                              <span className="gs-flag">under-indexing</span>
                            )}
                            {state !== NOT_CARRYING && state !== UNDERWEIGHT && (
                              <span className="gs-ok">at or above norm</span>
                            )}
                          </td>
                          <td className="cell-number">
                            {gal(num(row['distributor_actual_gal']))}
                          </td>
                          <td className="cell-number">
                            {gal(num(row['distributor_expected_gal']))}
                          </td>
                          <td className="cell-number gs-short">
                            {gal(num(row['distributor_shortfall_gal']))}
                          </td>
                          <td className="cell-number">
                            {pct(num(row['distributor_pct_of_norm']))}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </section>
            </>
          )}

          <section className="gs-detail-section">
            <h3 className="gs-comp-title">
              Every category this market carries, and who sells it
            </h3>
            <p className="gs-section-note">
              The mix, not just the gaps — a market can be a strong orange-juice market with a
              lime problem, and a gap list alone never says which.
            </p>
            {selectedLines.map((line) => {
              const category = text(line['mkt_product_line']) ?? NONE
              const sellers = selectedLineWho.filter(
                (row) => text(row['mkt_product_line']) === category,
              )
              const gapped = text(line['has_gap']) === 'yes'
              return (
                <div className="gs-mix" key={category}>
                  <div className="gs-mix-head">
                    <span className="gs-mix-name">
                      {category}
                      {gapped && <span className="gs-flag gs-mix-flag">gap</span>}
                    </span>
                    <span className="gs-mix-figs cell-number">
                      {gal(num(line['gallons_ttm']))} gal · {pct(num(line['share_of_market']))} of
                      market · {count(line['distributors'])} distributors
                    </span>
                  </div>
                  <ul className="gs-mix-sellers">
                    {sellers.map((row) => (
                      <li key={text(row['mkt_distributor']) ?? ''}>
                        <span className="gs-mix-seller">{text(row['mkt_distributor']) ?? NONE}</span>
                        <span className="gs-mix-track">
                          <span
                            className="gs-mix-fill"
                            style={{ width: `${String(num(row['share_of_line']) ?? 0)}%` }}
                          />
                        </span>
                        <span className="gs-mix-val cell-number">
                          {gal(num(row['gallons_ttm']))}
                        </span>
                        <span className="gs-mix-pct cell-number">
                          {pct(num(row['share_of_line']))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </section>

          <section className="gs-detail-section">
            <h3 className="gs-comp-title">Every distributor in this market</h3>
            <table className="gs-table">
              <thead>
                <tr>
                  <th scope="col">Distributor</th>
                  <th scope="col" className="cell-number">
                    Street gal TTM
                  </th>
                  <th scope="col" className="cell-number">
                    Share
                  </th>
                  <th scope="col" className="cell-number">
                    Ship-tos
                  </th>
                  <th scope="col" className="cell-number">
                    Lines
                  </th>
                  <th scope="col" className="cell-number">
                    Orders
                  </th>
                  <th scope="col" className="cell-number">
                    Last order
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedDistributors.map((row) => (
                  <tr key={text(row['mkt_distributor']) ?? ''}>
                    <td>{text(row['mkt_distributor']) ?? NONE}</td>
                    <td className="cell-number">{gal(num(row['gallons_ttm']))}</td>
                    <td className="cell-number">{pct(num(row['share_of_market']))}</td>
                    <td className="cell-number">{count(row['ship_tos'])}</td>
                    <td className="cell-number">{count(row['lines'])}</td>
                    <td className="cell-number">{count(row['orders'])}</td>
                    <td className="cell-number">{text(row['last_ship_date']) ?? NONE}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="gs-detail-section">
            <h3 className="gs-comp-title">Ship-tos in this market</h3>
            <table className="gs-table">
              <thead>
                <tr>
                  <th scope="col">Ship-to</th>
                  <th scope="col">Distributor</th>
                  <th scope="col" className="cell-number">
                    Street gal TTM
                  </th>
                  <th scope="col" className="cell-number">
                    Lines
                  </th>
                  <th scope="col" className="cell-number">
                    Orders
                  </th>
                  <th scope="col" className="cell-number">
                    Last order
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedShipTos.map((row) => {
                  const who = text(row['mkt_ship_to'])
                  const isOpen = who !== null && who === openShipTo
                  return (
                    <Fragment key={who ?? ''}>
                      <tr className={isOpen ? 'gs-row is-selected' : 'gs-row'}>
                        <td>
                          <button
                            type="button"
                            className="gs-link"
                            aria-expanded={isOpen}
                            onClick={() => {
                              setShipTo(isOpen ? null : who)
                            }}
                          >
                            <span aria-hidden="true">{isOpen ? '\u25be' : '\u25b8'}</span>{' '}
                            {who ?? NONE}
                          </button>
                        </td>
                        <td>{text(row['mkt_distributor']) ?? NONE}</td>
                        <td className="cell-number">{gal(num(row['mkt_gallons_ttm']))}</td>
                        <td className="cell-number">{count(row['mkt_lines'])}</td>
                        <td className="cell-number">{count(row['mkt_orders'])}</td>
                        <td className="cell-number">{text(row['mkt_last_ship_date']) ?? NONE}</td>
                      </tr>
                      {isOpen && (
                        <tr className="gs-mix-row">
                          <td colSpan={6}>
                            {shipToMix.length === 0 ? (
                              <p className="gs-note">
                                Nothing shipped from this branch in the trailing year.
                              </p>
                            ) : (
                              <>
                                <p className="gs-note">
                                  What this branch carries, against the market around it rather
                                  than the channel. A depot is judged against the other depots
                                  serving the same restaurants, which is the comparison a rep can
                                  act on.
                                </p>
                                <table className="gs-table gs-mix">
                                  <thead>
                                    <tr>
                                      <th scope="col">Category</th>
                                      <th scope="col" className="cell-number">
                                        Gallons TTM
                                      </th>
                                      <th scope="col" className="cell-number">
                                        Share here
                                      </th>
                                      <th scope="col" className="cell-number">
                                        Share across the market
                                      </th>
                                      <th scope="col" className="cell-number">
                                        Gap
                                      </th>
                                      <th scope="col" className="cell-number">
                                        Of the market in this line
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {shipToMix.map((line) => {
                                      const gap = num(line['mix_gap'])
                                      return (
                                        <tr key={text(line['mkt_product_line']) ?? ''}>
                                          <td>{text(line['mkt_product_line']) ?? NONE}</td>
                                          <td className="cell-number">
                                            {gal(num(line['gallons_ttm']))}
                                          </td>
                                          <td className="cell-number">
                                            {pct(num(line['pct_of_branch_mix']))}
                                          </td>
                                          <td className="cell-number">
                                            {pct(num(line['pct_of_market_mix']))}
                                          </td>
                                          <td
                                            className={
                                              gap !== null && gap < 0
                                                ? 'cell-number gs-short'
                                                : 'cell-number'
                                            }
                                          >
                                            {pct(gap)}
                                          </td>
                                          <td className="cell-number">
                                            {pct(num(line['share_of_line']))}
                                          </td>
                                        </tr>
                                      )
                                    })}
                                  </tbody>
                                </table>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </section>
        </Card>
      )}

      {excluded.length > 0 && (
        <Card
          title={`Excluded — shipped via a consolidator (${String(excluded.length)})`}
          subtitle={
            'Branches booked to a drop point in another state. They are taken out of every ' +
            'market figure above, because there is no address to move them to — the ones ' +
            'that had a correctable address were fixed upstream and already sit in their own ' +
            'metros. Mostly Canadian branches served through a US warehouse.'
          }
        >
          <div className="gs-scroll">
            <table className="gs-table">
              <thead>
                <tr>
                  <th scope="col">Ship-to</th>
                  <th scope="col">Distributor</th>
                  <th scope="col">Booked to</th>
                  <th scope="col" className="cell-number">
                    Zip
                  </th>
                  <th scope="col" className="cell-number">
                    Gallons TTM
                  </th>
                  <th scope="col" className="cell-number">
                    Lines
                  </th>
                  <th scope="col" className="cell-number">
                    Last order
                  </th>
                </tr>
              </thead>
              <tbody>
                {excluded.map((row) => (
                  <tr key={text(row['mkt_ship_to']) ?? ''}>
                    <td>{text(row['mkt_ship_to']) ?? NONE}</td>
                    <td>{text(row['mkt_distributor']) ?? NONE}</td>
                    <td className="gs-dim">{text(row['cbsa_title']) ?? NONE}</td>
                    <td className="cell-number">{text(row['mkt_ship_to_zip']) ?? NONE}</td>
                    <td className="cell-number">{gal(num(row['mkt_gallons_ttm']))}</td>
                    <td className="cell-number">{count(row['mkt_lines'])}</td>
                    <td className="cell-number">{text(row['mkt_last_ship_date']) ?? NONE}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card title="As of" subtitle="What each figure on this page was measured from.">
        <table className="gs-table gs-asof">
          <thead>
            <tr>
              <th scope="col">Source</th>
              <th scope="col">As of</th>
              <th scope="col">Age</th>
              <th scope="col">What it means</th>
            </tr>
          </thead>
          <tbody>
            {asOf.map((row) => (
              <tr key={text(row['source']) ?? ''}>
                <td>{text(row['source']) ?? NONE}</td>
                <td className="cell-number">{text(row['as_of']) ?? NONE}</td>
                <td>{text(row['age']) ?? ''}</td>
                <td className="gs-note">{text(row['note']) ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}
